// The picture side of the video noise reducer: WebGL2 passes that clean one frame.
//   1. temporal: the frame is averaged with the cleaned frame before it, but only where the two agree (a still wall, a dark sky) - where something moved they differ by far
//      more than noise does and the new frame is kept whole, so nothing ghosts. This is what removes most noise from video: grain is different in every frame, the picture is not.
//   2. spatial: an edge-preserving smoothing of brightness, and a wider one for colour (colour noise is coarser), both guided by the brightness edges;
//      optional smoothing across the 8-pixel block edges of compressed video;
//   3. detail: along strong edges the less smoothed picture is mixed back, so that lines and texture stay crisp.
import { Upscaler, program } from "../upscale/gl";

const TEMPORAL = `#version 300 es
precision highp float;
uniform sampler2D cur; uniform sampler2D prev; uniform vec2 px; uniform float t; uniform float sg;
in vec2 uv; out vec4 o;
float D(vec3 a, vec3 b) { vec3 d = abs(a - b); return (d.r + d.g + d.b) / 3.0; }     // all the colours, not only the brightness: a red ball on a grey wall moves without changing brightness
void main() {
  vec3 c = texture(cur, uv).rgb;
  vec3 p = texture(prev, uv).rgb;
  float acc = 0.0;
  for (int j = -1; j <= 1; j++) for (int i = -1; i <= 1; i++) {
    vec2 q = uv + vec2(float(i), float(j)) * px;
    acc += D(texture(cur, q).rgb, texture(prev, q).rgb);
  }
  float k = (acc / 9.0) / (3.0 * sg + 0.015);       // how much the two frames differ, in units of what noise alone would do
  float w = t * 0.9 * exp(-k * k);
  o = vec4(mix(c, p, w), 1.0);
}`;

const SPATIAL = `#version 300 es
precision highp float;
uniform sampler2D tex; uniform vec2 px; uniform vec2 size;
uniform float sp; uniform float ch; uniform float detail; uniform float deblock; uniform float sg;
in vec2 uv; out vec4 o;
vec3 toYcc(vec3 c) { float y = dot(c, vec3(0.299, 0.587, 0.114)); return vec3(y, (c.b - y) * 0.565, (c.r - y) * 0.713); }
vec3 fromYcc(vec3 q) { float b = q.y / 0.565 + q.x; float r = q.z / 0.713 + q.x; float g = (q.x - 0.299 * r - 0.114 * b) / 0.587; return vec3(r, g, b); }
void main() {
  vec3 c = texture(tex, uv).rgb;
  // smoothing across the edges of 8x8 blocks, but only where the step is small (a real edge that happens to lie on a block edge is kept)
  if (deblock > 0.01) {
    vec2 cell = floor(uv * size);
    vec2 m = vec2(mod(cell.x, 8.0), mod(size.y - 1.0 - cell.y, 8.0));         // counted from the top, as the blocks are
    vec3 acc = c; float ws = 1.0;
    float ex = m.x > 6.5 ? 1.0 : (m.x < 0.5 ? -1.0 : 0.0);
    if (ex != 0.0) { vec3 n = texture(tex, uv + vec2(ex * px.x, 0.0)).rgb; float k = (1.0 - smoothstep(0.03, 0.12, length(n - c))) * deblock; acc += n * k; ws += k; }
    float ey = m.y > 6.5 ? -1.0 : (m.y < 0.5 ? 1.0 : 0.0);                    // (rows count downwards in the picture, upwards on the screen)
    if (ey != 0.0) { vec3 n = texture(tex, uv + vec2(0.0, ey * px.y)).rgb; float k = (1.0 - smoothstep(0.03, 0.12, length(n - c))) * deblock; acc += n * k; ws += k; }
    c = acc / ws;
  }
  vec3 y0 = toYcc(c);
  // brightness: a bilateral filter over 5x5; the range it tolerates follows the strength and the noise level
  float sr = (0.012 + 0.2 * sp) * clamp(0.5 + 8.0 * sg, 0.7, 1.5);
  float accY = y0.x; float wsY = 1.0;
  for (int j = -2; j <= 2; j++) for (int i = -2; i <= 2; i++) {
    if (i == 0 && j == 0) continue;
    float q = toYcc(texture(tex, uv + vec2(float(i), float(j)) * px).rgb).x;
    float d = q - y0.x;
    float w = exp(-float(i * i + j * j) * 0.25) * exp(-d * d / (2.0 * sr * sr));
    accY += q * w; wsY += w;
  }
  // colour: a wider filter (9x9 reach), guided by the brightness so that colours do not bleed over edges
  float sc = 0.02 + 0.2 * ch;
  vec2 accC = y0.yz; float wsC = 1.0;
  for (int j = -2; j <= 2; j++) for (int i = -2; i <= 2; i++) {
    if (i == 0 && j == 0) continue;
    vec3 q = toYcc(texture(tex, uv + vec2(float(i), float(j)) * 2.0 * px).rgb);
    float dy = q.x - y0.x; vec2 dc = q.yz - y0.yz;
    float w = exp(-float(i * i + j * j) * 0.12) * exp(-dy * dy / 0.02) * exp(-dot(dc, dc) / (2.0 * sc * sc));
    accC += q.yz * w; wsC += w;
  }
  float ys = mix(y0.x, accY / wsY, smoothstep(0.0, 0.1, sp));
  vec2 cs = mix(y0.yz, accC / wsC, ch);
  vec3 den = fromYcc(vec3(ys, cs));
  // along strong edges the less smoothed picture is mixed back
  float gx = abs(toYcc(texture(tex, uv + vec2(px.x, 0.0)).rgb).x - toYcc(texture(tex, uv - vec2(px.x, 0.0)).rgb).x);
  float gy = abs(toYcc(texture(tex, uv + vec2(0.0, px.y)).rgb).x - toYcc(texture(tex, uv - vec2(0.0, px.y)).rgb).x);
  float keep = smoothstep(0.05, 0.22, gx + gy) * detail * 0.85;
  o = vec4(clamp(mix(den, c, keep), 0.0, 1.0), 1.0);
}`;

export class Denoiser extends Upscaler {
  constructor(canvas) {
    super(canvas);
    this.temporal = program(this.gl, TEMPORAL);
    this.spatial = program(this.gl, SPATIAL);
    this.latest = 0;                      // which of the two history pictures is the newest
    this.lastTime = -1;
    this.histSize = [0, 0];
  }

  setOutput() { /* the picture keeps the video's own size: render() sets it */ }

  // Draws the cleaned `source` (a playing or paused video) into the canvas, which has the size of the video. `p`: { temporal, spatial, chroma, detail, deblock, sigma } (0..1; sigma = the noise level).
  render(source, p) {
    const { gl } = this;
    const sw = source.videoWidth || source.naturalWidth || source.width;
    const sh = source.videoHeight || source.naturalHeight || source.height;
    if (!sw || !sh) return;
    if (this.canvas.width !== sw || this.canvas.height !== sh) { super.setOutput(sw, sh); }
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.bindTexture(gl.TEXTURE_2D, this.srcTexture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
    this.srcSize = [sw, sh];

    // is this the next frame of the same stretch of video, the same frame again, or somewhere else in the video (then there is no frame before it to average with)?
    const time = typeof source.currentTime === "number" ? source.currentTime : 0;
    const dt = time - this.lastTime;
    const same = this.histSize[0] === sw && this.histSize[1] === sh;
    const fresh = !same || this.lastTime < 0 || dt < -0.0005 || dt > 0.25 || p.temporal <= 0.01;
    const advance = !fresh && dt > 0.0005;
    if (advance) this.latest ^= 1;
    const names = ["histA", "histB"];
    const newest = this.target(names[this.latest], sw, sh);
    const older = this.target(names[this.latest ^ 1], sw, sh);
    this.histSize = [sw, sh];
    const uniforms = { px: [1 / sw, 1 / sh], sg: p.sigma, t: fresh ? 0 : p.temporal };
    this.pass(this.temporal, newest, [["cur", this.srcTexture], ["prev", older.tex]], uniforms, sw, sh);
    if (fresh) this.pass(this.temporal, older, [["cur", this.srcTexture], ["prev", newest.tex]], uniforms, sw, sh);   // no history: both are this frame
    if (fresh || advance) this.lastTime = time;
    this.pass(this.spatial, null, [["tex", newest.tex]], { px: [1 / sw, 1 / sh], size: [sw, sh], sp: p.spatial, ch: p.chroma, detail: p.detail, deblock: p.deblock, sg: p.sigma }, sw, sh);
  }
}
